import React, {forwardRef, useImperativeHandle, useRef, useContext, useEffect, useCallback} from 'react';
import {FetchContext} from '../context/FetchContext';
import {LocalVideoRecording} from './LocalVideoRecording';
import {startBackgroundUploader, resumePendingOnInit} from './LocalRecordingUploader';
import {LocalRecordingDBInstance, getUploadProgress} from './LocalRecordingStorage';
import type {AxiosRequestConfig} from 'axios';
import { updateParticipantStatus, type LocalRecordingErrorCode } from '../pages/NewStudio/ParticipantStatusService';

let uploaderStartedVideo = false;

const LocalVideoRecordingCompat = forwardRef(function LocalVideoRecordingCompat(props: any, ref) {
  const {
    room,
    livekitToken,                  // used as uploadToken
    localRecordingEnabled,
    setRecordingDone,
    setLocalRecordingStopped,
    isHDQuality,
    setUploadProgress,
  } = props;

  const fetchContext = useContext(FetchContext);
  const recorderRef = useRef<LocalVideoRecording | null>(null);
  const activeRef = useRef(false);
  const startingRef = useRef(false);
  const trackIdRef = useRef<string | null>(null);
  const pendingServerTrackIdRef = useRef<string | null>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const reportLocalRecordingError = useCallback(async (code: LocalRecordingErrorCode | '') => {
    try {
      const roomName = (room as any)?.name || (room as any)?.roomName;
      const participantIdentifier = (room as any)?.localParticipant?.identity;
      if (!roomName || !participantIdentifier || !livekitToken) return;
      await updateParticipantStatus(livekitToken, {
        roomName,
        participantIdentifier,
        localRecordingError: code,
      });
    } catch (e) {
      console.warn('[LocalVideoRecordingCompat] Failed to update participant status for local recording error', e);
    }
  }, [room, livekitToken]);

  useEffect(() => {
    if (uploaderStartedVideo) return;
    uploaderStartedVideo = true;
    startBackgroundUploader({
      onComplete: async (trackId, totalChunksCount) => {
        const rec = await LocalRecordingDBInstance.recordings.get(trackId);
        const uploadToken = rec?.uploadToken ?? livekitToken;
        if (!uploadToken) return;
        const config: AxiosRequestConfig = { headers: { Authorization: `Bearer ${uploadToken}` } };
        const serverTrackId =
          rec?.serverTrackId ??
          (rec?.trackId && !rec.trackId.startsWith('local-') ? rec.trackId : null);
        if (!serverTrackId) throw new Error('server trackId unavailable for /recording/complete');
        await fetchContext.authAxios.post('/recording/complete', { trackId: serverTrackId, totalChunksCount }, config);
      },
    });
    void resumePendingOnInit();
  }, [fetchContext, livekitToken]);

  // Poll and report upload progress
  const pollProgress = useCallback(async () => {
    if (!trackIdRef.current) return;
    
    try {
      const { progress, isComplete } = await getUploadProgress(trackIdRef.current);
      setUploadProgress?.(progress);
      
      if (isComplete) {
        // All chunks uploaded locally - stop polling for progress
        // NOTE: We do NOT set recordingDone here anymore.
        // The guest's Firestore subscription will set localRecordingDone
        // when backend confirms via uploadCompleted: true
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
        console.log('[LocalVideoRecordingCompat] All chunks uploaded, waiting for backend confirmation');
      }
    } catch (e) {
      console.warn('Error polling upload progress:', e);
    }
  }, [setUploadProgress]);

  // Start polling when recording stops (uploads continue)
  const startProgressPolling = useCallback(() => {
    if (progressIntervalRef.current) return;
    progressIntervalRef.current = setInterval(pollProgress, 1000);
    // Immediate first poll
    pollProgress();
  }, [pollProgress]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, []);

  useImperativeHandle(ref, () => ({
    startRecording: async () => {
      if (!localRecordingEnabled) return;
      if (activeRef.current || startingRef.current) return;
      startingRef.current = true;
      void reportLocalRecordingError(''); // clear any previous error indicator
      const rec = new LocalVideoRecording({
        room,
        fetchContext,
        uploadToken: livekitToken,
        isHDQuality,
        fps: 30,
        chunkDurationSec: 10,  // 10 second chunks for more visible upload progress
        batchSize: 50,
        onFatalError: (err) => {
          // Stop local capture but keep uploads running for any persisted segments.
          console.warn('[LocalVideoRecordingCompat] Local recording fatal error', err);
          void reportLocalRecordingError('fatal');
          setLocalRecordingStopped?.(true);
          recorderRef.current = null;
          activeRef.current = false;
          startingRef.current = false;
          startProgressPolling();
        },
      });
      try {
        await rec.start();
      } catch (e: any) {
        console.warn('[LocalVideoRecordingCompat] Failed to start local recording', e);
        void reportLocalRecordingError('start_failed');
        recorderRef.current = null;
        activeRef.current = false;
        startingRef.current = false;
        setLocalRecordingStopped?.(true);
        return;
      }
      // Apply deferred server trackId (delivered via Firestore) if we got it before start.
      if (pendingServerTrackIdRef.current) {
        rec.onTrackIdReceived(pendingServerTrackIdRef.current);
        pendingServerTrackIdRef.current = null;
      }
      recorderRef.current = rec;
      activeRef.current = true;
      startingRef.current = false;
      
      // Store trackId for progress polling
      trackIdRef.current = rec.getLocalRecordingId();
      
      // Start polling for upload progress during recording
      // (chunks are uploaded in real-time as they're recorded)
      startProgressPolling();
    },
    stopRecording: async () => {
      console.log("Stopping local video recording...");
      if (!activeRef.current) return;
      try {
        await recorderRef.current?.stop();
      } finally {
        console.log("Setting recording done flags...");
        recorderRef.current = null;
        activeRef.current = false;
        setLocalRecordingStopped?.(true);
        
        // Continue polling for remaining uploads
        // (polling already started during recording, but ensure it's running)
        startProgressPolling();
      }
    },
    isHighQualityRecordingActive: () => {
      return !!activeRef.current;
    },
    getTrackId: () => trackIdRef.current,
    onTrackIdReceived: (trackId: string) => {
      pendingServerTrackIdRef.current = trackId;
      recorderRef.current?.onTrackIdReceived(trackId);
    },
  }));

  return null;
});

export default LocalVideoRecordingCompat;
